<script setup lang="ts">
  import { computed, ref, shallowRef, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import AppSidebar from './AppSidebar.vue'
  import AppShellMobileTrigger from './AppShellMobileTrigger.vue'
  import {
    provideAppShellSectionMenu,
    SidebarProvider,
    type AppShellSectionMenu
  } from '@gat-ship/vue-components'
  import { useUser } from '@gat-ship/user'

  const user = useUser()

  const isRail = ref(false)

  // Section layouts register their menu label here while their sidebar
  // is teleported into the mobile Sheet (see app-shell-contract).
  // shallowRef: a deep ref would proxy the registered object and break
  // the layouts' identity-guarded cleanup (`ref.value === mine`).
  const sectionMenu = shallowRef<AppShellSectionMenu | null>(null)
  provideAppShellSectionMenu(sectionMenu)

  // Section layouts (admin, mail) render their own secondary sidebar and
  // declare `meta.railMainSidebar` on their route record. Entering such a
  // section forces the main sidebar to rail once; leaving it forces expand
  // once; in between the user's manual toggle stays free. Watching the
  // record (not a boolean) also re-forces rail when navigating directly
  // between two sections, mirroring the retired setSubMenuContent
  // mount/unmount semantics without their ordering race.
  const route = useRoute()
  const railRecord = computed(() =>
    route.matched.findLast((record) => record.meta.railMainSidebar === true)
  )
  watch(railRecord, (record) => (isRail.value = record !== undefined), {
    immediate: true
  })

  // The shadcn SidebarProvider runs in controlled mode: AppShell owns the
  // state (`isRail`), so the provider's `sidebar_state` cookie is written
  // but never read back — rail state intentionally resets to expanded on
  // every load, and route-meta forced rail stays authoritative.
  const sidebarOpen = computed({
    get: () => !isRail.value,
    set: (open: boolean) => (isRail.value = !open)
  })
</script>

<template>
  <SidebarProvider
    v-model:open="sidebarOpen"
    class="h-screen min-h-0 w-screen"
    :style="{
      '--sidebar-width': '280px',
      '--sidebar-width-icon': '64px',
      paddingTop: user.isSupportSession.value
        ? 'var(--app-status-bar-height)'
        : '0px'
    }"
  >
    <!-- Main Menu -->
    <slot name="main-menu">
      <AppSidebar>
        <template #main-menu-content>
          <slot name="main-menu-content" />
        </template>
      </AppSidebar>
    </slot>

    <!-- Mobile-only floating trigger: the shell renders no other nav
    chrome below md, so this opens the main menu as a Sheet. -->
    <AppShellMobileTrigger />

    <!-- Main Content -->
    <main class="flex h-full min-h-0 w-full min-w-0 flex-col overflow-auto">
      <slot name="main" />
    </main>
  </SidebarProvider>
</template>
